import React from "react";
import { alpha, Box, styled, Typography } from "@mui/material";
import { ItemType } from "../../types/types";
import Description from "./Description";
import PhoneSpecs from "./PhoneSpecs";
import { StyledDivider } from "./styledComponents";
import { PRODUCT_DETAIL_TEXTS } from "../../pages/shared/constants";

/**
 * The description and specs band (EB-557).
 *
 * Like `ItemRelated` before it, this sat in the right-hand column of the two-column grid and had
 * half the page to work with — while the left half sat empty from the end of the carousel down. On
 * a 15-row phone the specs table, which already asks for two columns at `sm`, got about a quarter
 * of the viewport per column and broke "12+12+12 MP Triple (Wide, Ultra Wide, Telephoto 2x)" over
 * four lines. This resolves the question left open in
 * `docs/product-detail-redesign/README.md` §4.
 *
 * The buy block stays in the column: it is the decision, and it has to be visible next to the
 * photos without scrolling (README §3). Only the reading material moves.
 *
 * There is no desktop/mobile variant — mobile was already one column and the order is unchanged.
 *
 * The separator lives here rather than in `Item`, for the same reason `RelatedProducts` owns its
 * own: a product with neither description nor specs renders nothing, and a rule left behind by an
 * absent band is a line across the page attached to nothing.
 */
type ItemInfoProps = {
  item: ItemType;
};

/**
 * A surface under the reading material (EB-580).
 *
 * Everything on this card — gallery, price, buy buttons, delivery estimate — is painted on the same
 * `background.paper` cream, and since EB-557 put this band at full width it became the longest flat
 * stretch on the page with nothing but a divider between it and the decision block above. A white
 * pane lifts the description and the specs off that cream and marks them as the part you read rather
 * than the part you act on. See `docs/product-detail-text-surfaces/README.md`.
 *
 * The fill is `background.default` rather than a new theme constant: it is exactly the page white,
 * and a `PANEL_WHITE = "#ffffff"` next to `GRAY_VISUAL` would name a colour the palette already
 * has. The hairline is derived from `text.primary` for the same reason — the border belongs to the
 * navy family the card is drawn in, and `alpha()` keeps that link visible instead of freezing it
 * into an opaque hex.
 *
 * The specs banding is deliberately untouched. `action.hover` is a 4% black wash, which still reads
 * as a stripe on white; it is only the grey-panel variant that would have needed it inverted.
 */
const InfoPanel = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
}));

const ItemInfo: React.FC<ItemInfoProps> = ({ item }) => {
  const hasDescription = Boolean(item.description);
  const hasSpecs = Boolean(item.specs && Object.keys(item.specs).length > 0);

  if (!hasDescription && !hasSpecs) return null;

  return (
    <Box width="100%">
      <StyledDivider />
      {/* Same horizontal padding as `RelatedProducts`, so the bands below the grid share a margin.
          The rule itself deliberately spans the full width, as it does there. */}
      <Box px={2} pt={1} pb={2}>
        {/* Two panels rather than one around both: a product with a description and no specs, or
            specs and no description, then gets a panel that fits what it has — and the gap between
            them is what tells the eye the specs table is a separate thing from the prose. */}
        {hasDescription && (
          <InfoPanel>
            <Typography variant="h6" fontWeight="bold">
              {PRODUCT_DETAIL_TEXTS.descriptionTitle}
            </Typography>
            <Description item={item} />
          </InfoPanel>
        )}
        {hasSpecs && (
          <InfoPanel mt={hasDescription ? 2 : 0}>
            <PhoneSpecs item={item} />
          </InfoPanel>
        )}
      </Box>
    </Box>
  );
};

export default ItemInfo;
